/**
 * flujo.ts — qué pantalla toca, dado lo que se sabe.
 *
 * Está separado del componente para poder probarlo. No es purismo: el acceso
 * tiene seis estados y cinco de ellos son transitorios o de error, así que en
 * la práctica solo se ven abriendo la aplicación en condiciones raras — sin
 * configurar, con la sesión a medio comprobar, con una empresa recordada que
 * ya no existe. Son justo los que nadie prueba a mano.
 *
 * ── La regla que ordena todo esto ─────────────────────────────────────────
 *
 * **Falta de dato no es «no».** Mientras no se sepa si hay sesión, la pantalla
 * es «cargando», no «entrar». Enseñar el formulario de acceso a alguien que ya
 * tiene sesión lo hace teclear su contraseña otra vez sin motivo, y —peor— si
 * la teclea mal cree que perdió el acceso.
 *
 * Es la misma idea del latido y del silencio del hub: la ausencia de un dato
 * es un estado con nombre, no la respuesta negativa.
 */

export interface EmpresaDisponible {
  readonly id: string;
  readonly codigo: string;
  readonly razonSocial: string;
  readonly rol: 'admin' | 'contador' | 'solo_lectura';
  readonly estado: 'activa' | 'suspendida' | 'cerrada';
  /**
   * La moneda en que se llevan ESTOS libros. No es una preferencia y no se
   * supone: se fija al crear la empresa y no se puede cambiar después del
   * primer asiento.
   *
   * Viaja con la empresa porque todo el dinero de la aplicación se rotula con
   * ella. Antes salía de `datos/muestra.ts`, así que la pantalla afirmaba
   * «colones» sin que nadie lo hubiera elegido — y para una empresa en dólares
   * cada importe habría salido con la moneda equivocada, con la misma cara que
   * uno correcto.
   */
  readonly monedaLocal: string;
  /** La cédula jurídica, normalizada. Encabeza los estados financieros. */
  readonly identificacion: string | null;
  /**
   * Con qué idioma abre quien todavía NO eligió.
   *
   * Es una propuesta, no una imposición: la preferencia de la persona —guardada
   * en su navegador por el conmutador de la barra— manda sobre ésta. Ver
   * `proponerIdioma` en `nucleo/idioma.tsx`.
   */
  readonly idiomaPorOmision: 'es' | 'en';
  /**
   * Si esa identificación es un RELLENO y no la real.
   *
   * Viaja pegada al número a propósito: enseñar la cédula sin decir que es
   * provisional es presentar como cierto algo que nosotros mismos inventamos.
   */
  readonly identificacionProvisional: boolean;
}

export interface EstadoDeAcceso {
  /** ¿Están puestas las variables del proveedor de identidad? */
  readonly configurado: boolean;
  /** Todavía se está preguntando si hay sesión. */
  readonly comprobando: boolean;
  readonly autenticado: boolean;
  /** `null` mientras no se hayan consultado. Distinto de la lista vacía. */
  readonly empresas: readonly EmpresaDisponible[] | null;
  /** La que se recuerda de la última vez, o la que se acaba de elegir. */
  readonly empresaRecordada: string | null;
}

export type Pantalla =
  | 'sin_configurar'
  | 'cargando'
  | 'entrar'
  | 'sin_empresas'
  | 'elegir_empresa'
  | 'aplicacion';

/**
 * La empresa que de verdad se va a usar.
 *
 * ⚠️ Una empresa recordada NO se usa por recordada: tiene que seguir en la
 * lista que el servidor acaba de devolver. Si a alguien le quitaron la
 * membresía, lo que quedó guardado en su navegador no puede devolvérsela.
 *
 * El servidor la rechazaría igual —`abrir_sesion` comprueba la membresía en
 * cada petición— pero el resultado sería un 403 en una pantalla que ya se
 * pintó. Acá se cae limpio al selector.
 */
export function empresaEfectiva(e: EstadoDeAcceso): EmpresaDisponible | null {
  if (!e.empresas || e.empresas.length === 0) return null;

  const recordada = e.empresaRecordada
    ? e.empresas.find((x) => x.id === e.empresaRecordada)
    : undefined;
  if (recordada) return recordada;

  // Con una sola no se pregunta: elegir entre una opción no es una decisión.
  // Con varias sí, y a propósito — un asesor con veinte clientes tiene que ver
  // en cuáles libros está entrando.
  return e.empresas.length === 1 ? e.empresas[0]! : null;
}

export function pantallaPara(e: EstadoDeAcceso): Pantalla {
  // Primero, y siempre. Un formulario de acceso que no puede funcionar es peor
  // que una pantalla que explica qué falta: la persona teclea su contraseña,
  // falla, y concluye que la contraseña está mal.
  if (!e.configurado) return 'sin_configurar';

  if (e.comprobando) return 'cargando';
  if (!e.autenticado) return 'entrar';

  // Autenticado y todavía sin saber a qué empresas pertenece.
  if (e.empresas === null) return 'cargando';

  // Autenticado y sin ninguna. Es un estado real, no un error: alguien a quien
  // acaban de quitar de su única empresa, o una invitación que nunca se
  // completó. Sin pantalla propia, se vería un selector vacío.
  if (e.empresas.length === 0) return 'sin_empresas';

  return empresaEfectiva(e) ? 'aplicacion' : 'elegir_empresa';
}

/* ═══════════════════════════════════════════════════════════════════════════
   ▶ Configurado no es «la variable existe»
   ═══════════════════════════════════════════════════════════════════════════

   Esto empezó comprobando `Boolean(url && clave)`, y el `.env.example` de este
   mismo repositorio trae `https://....supabase.co` y `...` de marcadores.

   Los dos son cadenas no vacías. Así que quien copiara el ejemplo y no lo
   llenara vería el FORMULARIO DE ACCESO —no la pantalla de «falta
   configurar»— tecleando su contraseña buena contra un servidor que no
   existe, y concluyendo que la contraseña está mal.

   Es literalmente el fallo que la pantalla de «sin configurar» existe para
   evitar, y la comprobación lo dejaba pasar. */

/** Un marcador de posición del ejemplo, no un valor. */
function esMarcador(v: string): boolean {
  return v.includes('...') || v.includes('<') || v.includes('xxx');
}

/**
 * `true` solo si las dos variables tienen pinta de valores de verdad.
 *
 * No valida que funcionen —eso lo dice el primer intento de entrar— sino que
 * alguien las haya puesto. Es la diferencia entre «no está configurado» y
 * «está configurado y falló», que son dos mensajes distintos para dos
 * problemas distintos.
 */
export function configuracionValida(url: unknown, clave: unknown): boolean {
  if (typeof url !== 'string' || typeof clave !== 'string') return false;
  if (esMarcador(url) || esMarcador(clave)) return false;

  // La clave anónima es un token largo. Tres caracteres no es una clave.
  if (clave.trim().length < 20) return false;

  try {
    const u = new URL(url);
    /* ▶ `https:`, o `http:` SOLO contra la propia máquina.
     *
     * La excepción existe por `npm run caminata`, que sirve un Supabase de
     * mentira en `http://127.0.0.1` para poder abrir la aplicación sin un
     * proyecto de verdad. Sin esto, la caminata llega a «falta configurar el
     * acceso» y no se puede caminar nada — que es exactamente el defecto que
     * la caminata existe para encontrar, cometido por nosotros.
     *
     * Y no reabre el agujero que esta función vino a tapar. Lo que se colaba
     * era `https://....supabase.co`, el marcador del ejemplo, y sigue fuera
     * por dos sitios: `esMarcador` y la etiqueta vacía. Un Supabase real NUNCA
     * vive en la dirección de vuelta de la máquina, así que abrir el bucle no
     * deja pasar ninguna configuración que alguien pudiera creerse.
     *
     * Se escribe la lista de nombres y no `startsWith('127.')`: `127.0.0.1`
     * con otro puerto es lo mismo, pero `127.0.0.1.dominio-de-alguien.com` no
     * lo es, y esa es una comprobación que se equivoca en el sentido caro. */
    const bucle = ['127.0.0.1', 'localhost', '[::1]', '::1'];
    const enMiMaquina = bucle.includes(u.hostname);
    if (u.protocol !== 'https:' && !(u.protocol === 'http:' && enMiMaquina)) return false;
    // Un host con una etiqueta vacía —`....supabase.co`— no es un host.
    return u.hostname.split('.').every((parte) => parte.length > 0);
  } catch {
    return false;
  }
}
